import {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ClickableTableCell, DashboardTable } from "./dashboard-table";
import { SeverityChip } from "~/components/severity-chip";
import {
  AggregateScoreType,
  AggregateScoreOrderField,
  OrderDirection,
  useGetAggregateScoresQuery,
} from "~/operations";
import DashboardSkeleton from "~/pages/organization/dashboard/DashboardSkeleton";

function extractAdvisoryId(findingMrn: string): string {
  const parts = findingMrn.split("/");
  return parts[parts.length - 1] || findingMrn;
}

export type EndOfLifeProps = {
  organizationMrn: string;
};

export function EndOfLife({ organizationMrn }: EndOfLifeProps) {
  const result = useGetAggregateScoresQuery({
    variables: {
      entityMrn: organizationMrn,
      filter: {
        scoreTypes: [AggregateScoreType.Advisory],
        minRiskValue: 1,
        queryTerms: ["Mondoo-EOL"],
      },
      first: 10,
      skipCases: true,
      orderBy: {
        field: AggregateScoreOrderField.RiskValue,
        direction: OrderDirection.Asc,
      },
    },
  });

  if (result.loading) {
    return <DashboardSkeleton />;
  }

  const connection =
    result.data?.aggregateScores?.__typename === "AggregateScoresConnection"
      ? result.data.aggregateScores
      : null;

  const edges = connection?.edges || [];

  if (edges.length === 0) {
    return (
      <Typography
        sx={{
          fontWeight: "bold",
          color: "text.secondary",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        No end-of-life advisories found in your organization
      </Typography>
    );
  }

  return (
    <DashboardTable>
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: "10%", minWidth: "90px", pr: 0 }}>
            Risk Score
          </TableCell>
          <TableCell sx={{ width: "70%", pr: 0 }}>Advisory</TableCell>
          <TableCell sx={{ width: "20%", minWidth: "60px" }}>Assets</TableCell>
        </TableRow>
      </TableHead>
      <TableBody
        sx={[{ ".MuiTableCell-body": { fontSize: 12, cursor: "default" } }]}
      >
        {edges
          .filter((e): e is typeof e & { node: NonNullable<typeof e.node> } =>
            Boolean(e.node),
          )
          .map(({ node }) => {
            const advisoryId = extractAdvisoryId(node.findingMrn);
            const href = node.spaceId
              ? `/space/vulns/advisory/${advisoryId}?spaceId=${node.spaceId}`
              : undefined;
            return (
              <TableRow key={node.findingMrn}>
                <ClickableTableCell
                  sx={{ width: "10%", minWidth: "90px", pr: 0 }}
                  href={href}
                >
                  <SeverityChip severity={node.riskValue ?? undefined} small />
                </ClickableTableCell>
                <ClickableTableCell sx={{ width: "70%", pr: 0 }} href={href}>
                  <Typography
                    component="span"
                    fontWeight="bold"
                    fontSize="inherit"
                  >
                    {node.title}
                  </Typography>
                  {node.description && (
                    <Typography component="span" fontSize="inherit">
                      : {node.description}
                    </Typography>
                  )}
                </ClickableTableCell>
                <ClickableTableCell
                  sx={{ width: "20%", minWidth: "60px" }}
                  href={href}
                >
                  {node.blastRadius?.affected ?? 0}
                </ClickableTableCell>
              </TableRow>
            );
          })}
      </TableBody>
    </DashboardTable>
  );
}
